/**
 * Block 6: Reservation and Usage Time Section (03_13 - 03_23)
 * 
 * Requirements:
 * - 03_13: "予約・利用時間" section title
 * - 03_14: Bar chart showing reserved_rate and used_rate by room (stacked vertically)
 * - 03_15: Monthly data from meeting_room_mart_reserved
 * - 03_16: Sortable by room name or rate
 * - 03_17: Pagination (5 rooms per page, always visible)
 * - 03_18-03_23: Area chart showing 4-month trend with room selector
 */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { QUERY_STRING_STORE } from '@/helpers/constants';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import {
  fetchMeetingRoomMartReserved,
  getLastNMonths,
  formatMonthShort,
} from './query-request';
import './ReservationUsageChart.less';

@connect(state => ({
  querystring: state.querystring,
}))
class ReservationUsageChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      roomData: [],
      allData: [],
      trendData: [],
      selectedMonth: null,
      selectedRoom: null,
      availableMonths: [],
      availableRooms: [],
      sortField: 'room_name',
      sortDirection: 'asc',
      first: 0,
      rows: 5,
      hoveredBarRow: null,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    const prevBranch = prevProps?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];
    const currentBranch = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];
    if (prevBranch !== currentBranch) {
      this.fetchData();
    }
  }

  fetchData = async () => {
    const tenantId = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const branchId = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];

    if (!tenantId) {
      this.setState({ roomData: [], trendData: [], allData: [] });
      return;
    }

    this.setState({ isLoading: true });

    try {
      const months = getLastNMonths(4);
      const data = await fetchMeetingRoomMartReserved({ tenantId, branchId, months });

      if (!data || data.length === 0) {
        this.setState({ roomData: [], trendData: [], allData: [], isLoading: false });
        return;
      }

      // Get unique months for dropdown
      const uniqueMonths = [...new Set(data.map(d => d.month))].sort().reverse();
      const selectedMonth = this.state.selectedMonth || uniqueMonths[0];

      // Get unique rooms for dropdown (including branch total)
      const roomsMap = new Map();
      data.forEach(d => {
        if (d.room_name) {
          const key = d.is_branch_total ? '__BRANCH_TOTAL__' : d.device_identifier;
          if (!roomsMap.has(key)) {
            roomsMap.set(key, {
              label: d.is_branch_total ? `${d.room_name}（合計）` : d.room_name,
              value: key,
              is_branch_total: d.is_branch_total,
            });
          }
        }
      });

      // Sort rooms: branch total first, then by name
      const availableRooms = Array.from(roomsMap.values()).sort((a, b) => {
        if (a.is_branch_total) return -1;
        if (b.is_branch_total) return 1;
        return a.label.localeCompare(b.label);
      });

      const selectedRoom = this.state.selectedRoom || (availableRooms[0]?.value || null);

      // Filter data for selected month (include branch totals for room chart)
      const monthData = data.filter(d => d.month === selectedMonth);

      // Transform for bar chart - using rates (percentages)
      // Sort: branch total first, then by room name
      const roomData = monthData
        .map(d => ({
          room_name: d.room_name || 'Unknown',
          device_identifier: d.device_identifier,
          is_branch_total: d.is_branch_total || false,
          reserved_rate: Math.round((d.reserved_rate || 0) * 100),
          used_rate: Math.round((d.used_rate || 0) * 100),
        }))
        .sort((a, b) => {
          // Branch total always first
          if (a.is_branch_total && !b.is_branch_total) return -1;
          if (!a.is_branch_total && b.is_branch_total) return 1;
          // Then sort by room name
          return a.room_name.localeCompare(b.room_name);
        });

      // Transform for trend chart based on selected room
      const trendData = this.getTrendDataForRoom(data, selectedRoom);

      this.setState({
        roomData: this.sortData(roomData),
        allData: data,
        trendData,
        availableMonths: uniqueMonths.map(m => ({ label: formatMonthShort(m), value: m })),
        availableRooms,
        selectedMonth,
        selectedRoom,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching reservation data:', error);
      this.setState({ roomData: [], trendData: [], allData: [], isLoading: false });
    }
  };

  getTrendDataForRoom = (data, roomKey) => {
    let filteredData;
    if (roomKey === '__BRANCH_TOTAL__') {
      filteredData = data.filter(d => d.is_branch_total);
    } else {
      filteredData = data.filter(d => d.device_identifier === roomKey && !d.is_branch_total);
    }

    return filteredData
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(d => {
        const date = new Date(d.month);
        const year = date.getFullYear();
        const monthNum = date.getMonth() + 1;
        return {
          month: `${monthNum}月`,
          monthFull: d.month,
          yearMonth: `${year}年${monthNum}月`,
          reserved_rate: Math.round((d.reserved_rate || 0) * 100),
          used_rate: Math.round((d.used_rate || 0) * 100),
        };
      });
  };

  sortData = (data) => {
    const { sortField, sortDirection } = this.state;
    return [...data].sort((a, b) => {
      // Branch total always stays first regardless of sort
      if (a.is_branch_total && !b.is_branch_total) return -1;
      if (!a.is_branch_total && b.is_branch_total) return 1;

      const aVal = a[sortField];
      const bVal = b[sortField];
      const result = typeof aVal === 'string' ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === 'asc' ? result : -result;
    });
  };

  handleSort = (field) => {
    this.setState(prevState => {
      const newDirection = prevState.sortField === field && prevState.sortDirection === 'asc' ? 'desc' : 'asc';
      return {
        sortField: field,
        sortDirection: newDirection,
        roomData: [...prevState.roomData].sort((a, b) => {
          // Branch total always stays first regardless of sort
          if (a.is_branch_total && !b.is_branch_total) return -1;
          if (!a.is_branch_total && b.is_branch_total) return 1;

          const aVal = a[field];
          const bVal = b[field];
          const result = typeof aVal === 'string' ? aVal.localeCompare(bVal) : aVal - bVal;
          return newDirection === 'asc' ? result : -result;
        }),
      };
    });
  };

  handleMonthChange = (e) => {
    this.setState({ selectedMonth: e.value, currentPage: 1 }, () => this.fetchData());
  };

  handleRoomChange = (e) => {
    const { allData } = this.state;
    const selectedRoom = e.value;
    const trendData = this.getTrendDataForRoom(allData, selectedRoom);

    this.setState({
      selectedRoom,
      trendData,
    });
  };

  handlePageChange = (e) => {
    this.setState({ first: e.first, rows: e.rows });
  };

  getSortIcon = (field) => {
    const { sortField, sortDirection } = this.state;
    if (sortField !== field) return '/assets/img/sort-icon.svg';
    return sortDirection === 'asc' ? '/assets/img/sort-up-icon.svg' : '/assets/img/sort-down-icon.svg';
  };


  renderBarChart = () => {
    const { roomData, first, rows, availableMonths, selectedMonth } = this.state;
    const paginatedData = roomData.slice(first, first + rows);

    return (
      <div className="bar-chart-section">
        {/* Header with month dropdown aligned right */}
        <div className="chart-header">
          <Dropdown
            value={selectedMonth}
            options={availableMonths}
            onChange={this.handleMonthChange}
            placeholder="月を選択"
            className="month-dropdown"
          />
        </div>

        {/* Table Header */}
        <div className="table-header">
          <div className="header-cell room-name-col">
            <span>部屋名</span>
            <button className="sort-btn" onClick={() => this.handleSort('room_name')}>
              <img src={this.getSortIcon('room_name')} alt="sort" />
            </button>
          </div>
          <div className="header-cell rate-col">
            <span className="reserved-label">予約時間</span>
            <button className="sort-btn" onClick={() => this.handleSort('reserved_rate')}>
              <img src={this.getSortIcon('reserved_rate')} alt="sort" />
            </button>
          </div>
          <div className="header-cell rate-col">
            <span className="used-label">利用時間</span>
            <button className="sort-btn" onClick={() => this.handleSort('used_rate')}>
              <img src={this.getSortIcon('used_rate')} alt="sort" />
            </button>
          </div>
        </div>

        {/* Table Body - Stacked bars with single tooltip for both */}
        {paginatedData.length === 0 ? (
          <div className="no-data-container">データがありません</div>
        ) : (
          <div className="table-body">
            {paginatedData.map((item, index) => {
              const roomKey = item.is_branch_total ? `${item.room_name}（合計）` : item.room_name;
              return (
                <div
                  key={index}
                  className="table-row"
                  onMouseEnter={() => this.setState({ hoveredBarRow: roomKey })}
                  onMouseLeave={() => this.setState({ hoveredBarRow: null })}
                >
                  <div className="cell room-name-col">
                    {roomKey}
                  </div>
                  <div className="cell bars-col">
                    <div className={`stacked-bars bars-container-${index}`}>
                      <div className="bar-row">
                        <div className="bar-bg">
                          <div
                            className="bar reserved-bar"
                            style={{ width: `${Math.max(3, item.reserved_rate)}%` }}
                          />
                        </div>
                      </div>
                      <div className="bar-row">
                        <div className="bar-bg">
                          <div
                            className="bar used-bar"
                            style={{ width: `${Math.max(3, item.used_rate)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    {this.state.hoveredBarRow === roomKey && (
                      <div className="custom-tooltip bar-tooltip">
                        <div className="tooltip-title">{roomKey}</div>
                        <div className="tooltip-row reserved-row">
                          <span className="tooltip-label">予約:</span>
                          <span className="tooltip-value">{item.reserved_rate}%</span>
                        </div>
                        <div className="tooltip-row used-row">
                          <span className="tooltip-label">利用:</span>
                          <span className="tooltip-value">{item.used_rate}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination - Always visible */}
        <Paginator
          first={first}
          rows={rows}
          totalRecords={roomData.length}
          onPageChange={this.handlePageChange}
          className="custom-paginator"
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
        />
      </div>
    );
  };

  // Custom tooltip for area chart
  CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{data?.yearMonth}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-item" style={{ color: entry.color }}>
              {entry.name === 'reserved_rate' ? '予約' : '利用'}: {entry.value}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  renderAreaChart = () => {
    const { trendData, availableRooms, selectedRoom } = this.state;

    return (
      <div className="area-chart-section">
        {/* Room Selector Dropdown */}
        <div className="room-selector">
          <Dropdown
            value={selectedRoom}
            options={availableRooms}
            onChange={this.handleRoomChange}
            placeholder="部屋を選択"
            className="room-dropdown"
          />
        </div>

        {/* Area Chart */}
        {trendData.length === 0 ? (
          <div className="no-data-container">データがありません</div>
        ) : (
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid stroke="#a2a8bb" strokeWidth={1} horizontal={true} vertical={true} />
                <XAxis
                  dataKey="month"
                  stroke="#a2a8bb"
                  tick={{ fontSize: 11 }}
                  axisLine={{ stroke: '#a2a8bb' }}
                  tickLine={{ stroke: '#a2a8bb' }}
                />
                <YAxis
                  stroke="#a2a8bb"
                  domain={[0, 100]}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => value === 0 ? '(%)' : `${value}`}
                  axisLine={{ stroke: '#a2a8bb' }}
                  tickLine={{ stroke: '#a2a8bb' }}
                  ticks={[0, 25, 50, 75, 100]}
                />
                <RechartsTooltip content={this.CustomTooltip} />
                {/* Reserved (orange) - top layer */}
                <Area
                  type="monotone"
                  dataKey="reserved_rate"
                  name="reserved_rate"
                  stroke="#e8946a"
                  fill="#e8946a"
                  fillOpacity={0.7}
                />
                {/* Used (red) - bottom layer */}
                <Area
                  type="monotone"
                  dataKey="used_rate"
                  name="used_rate"
                  stroke="#e74c3c"
                  fill="#e74c3c"
                  fillOpacity={0.7}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { isLoading } = this.state;

    return (
      <div className="reservation-usage-chart">
        <div className="section-header">
          <h3 className="section-title">予約・利用時間</h3>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="charts-container">
            {/* Left: Bar Chart with month dropdown */}
            <div className="left-section">
              {this.renderBarChart()}
            </div>

            {/* Right: Area Chart with room dropdown */}
            <div className="right-section">
              {this.renderAreaChart()}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default ReservationUsageChart;
